import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { useGPUTier } from '../../hooks/useGPUTier';
import { useConnectionSpeed } from '../../hooks/useConnectionSpeed';
import { getImagePath } from '../../utils/imagePath';
import { logger } from '../../utils/logger';

// GLSL shader for realistic TV static effect
const staticVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const staticFragmentShader = `
  uniform float time;
  uniform sampler2D logoTexture;
  uniform bool hasLogo;
  uniform float logoOpacity;
  uniform float textureRepeat;
  uniform vec2 textureOffset;
  varying vec2 vUv;
  
  // Multiple random functions for better randomness
  float random1(vec2 st) {
    return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
  }
  
  float random2(vec2 st) {
    return fract(sin(dot(st.xy, vec2(93.9898,45.233))) * 43758.5453123);
  }
  
  float random3(vec2 st) {
    return fract(sin(dot(st.xy, vec2(67.1234,89.4567))) * 43758.5453123);
  }
  
  // Improved noise with multiple random functions
  float noise(vec2 st) {
    vec2 i = floor(st);
    vec2 f = fract(st);
    
    float a = random1(i);
    float b = random2(i + vec2(1.0, 0.0));
    float c = random3(i + vec2(0.0, 1.0));
    float d = random1(i + vec2(1.0, 1.0));
    
    vec2 u = f * f * (3.0 - 2.0 * f);
    
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }
  
  // Generate enhanced scanlines with random variation (slower for CRT feel)
  float scanlines(vec2 uv, float time) {
    // Primary scanlines - more visible
    float scanlineFreq = 600.0 + sin(time * 0.2) * 30.0; // Slower frequency variation
    float scanline = sin(uv.y * scanlineFreq + time * 0.8) * 0.08; // More visible scanlines
    
    // Secondary scanlines for depth
    float scanline2 = sin(uv.y * 1200.0 + time * 0.4) * 0.03; // Finer scanlines
    
    // Add some horizontal banding for CRT authenticity
    float banding = sin(uv.y * 200.0 + time * 0.1) * 0.02;
    
    return scanline + scanline2 + banding;
  }
  
  // Pixelate UV coordinates to create blocky effect
  vec2 pixelate(vec2 uv, float pixelSize) {
    return floor(uv * pixelSize) / pixelSize;
  }
  
  // Random jitter with multiple directions (slower for CRT feel)
  vec2 jitter(vec2 uv, float time) {
    float jitterAmount = 0.0015;
    
    // Multiple jitter sources with slower frequencies
    uv.x += sin(uv.y * 120.0 + time * 2.0) * jitterAmount; // Much slower
    uv.y += cos(uv.x * 80.0 + time * 1.5) * jitterAmount; // Much slower
    uv.x += sin(uv.x * 200.0 + time * 3.0) * jitterAmount * 0.5; // Much slower
    uv.y += cos(uv.y * 150.0 + time * 2.5) * jitterAmount * 0.5; // Much slower
    
    return uv;
  }
  
  void main() {
    vec2 uv = vUv;
    
    // Apply texture scaling to match regular texture fitting
    uv = uv * textureRepeat + textureOffset;
    
    // Add slight barrel distortion to simulate CRT screen curvature
    vec2 center = vec2(0.5, 0.5);
    vec2 offset = uv - center;
    float distortion = 1.0 + dot(offset, offset) * 0.1; // Subtle barrel distortion
    uv = center + offset * distortion;
    
    // Add jitter for authenticity
    vec2 jitteredUv = jitter(uv, time);
    
    // Create pixelated versions for different static layers
    vec2 pixelatedFine = pixelate(jitteredUv, 120.0); // Small pixels
    vec2 pixelatedMedium = pixelate(jitteredUv, 60.0); // Medium pixels
    vec2 pixelatedCoarse = pixelate(jitteredUv, 30.0); // Large pixels
    vec2 pixelatedNoise = pixelate(jitteredUv, 80.0); // Medium-small pixels
    
    // Create multiple independent static layers with different random seeds (slower timing)
    // Fine grain static - small pixelated blocks
    float fineStatic = random1(pixelatedFine * 500.0 + time * 0.02); // Much slower
    fineStatic = pow(fineStatic, 3.0); // Make it very sparse
    
    // Medium grain static - medium pixelated blocks
    float mediumStatic = random2(pixelatedMedium * 200.0 + time * 0.01); // Much slower
    mediumStatic = pow(mediumStatic, 2.0);
    
    // Coarse grain static - large pixelated blocks
    float coarseStatic = random3(pixelatedCoarse * 80.0 + time * 0.005); // Much slower
    coarseStatic = pow(coarseStatic, 1.5);
    
    // Add some noise-based static for texture - medium pixelated
    float noiseStatic = noise(pixelatedNoise * 300.0 + time * 0.02); // Much slower
    noiseStatic = pow(noiseStatic, 2.5);
    
    // Combine static layers with realistic distribution
    float combinedStatic = fineStatic * 0.3 + mediumStatic * 0.3 + coarseStatic * 0.2 + noiseStatic * 0.2;
    
    // Add random temporal variation with pixelation (slower for CRT feel)
    vec2 pixelatedTemporal = pixelate(uv, 40.0); // Large pixels for temporal variation
    float temporalRandom = random1(pixelatedTemporal + floor(time * 2.0)); // Much slower temporal changes
    combinedStatic = mix(combinedStatic, temporalRandom, 0.1);
    
    // Create more realistic TV static intensity curve with pixelation
    vec2 pixelatedIntensity = pixelate(uv, 35.0); // Medium-large pixels for intensity
    float intensity;
    if (combinedStatic > 0.9) {
      // Very bright pixels (very rare)
      intensity = 0.95 + random2(pixelatedIntensity + time) * 0.05;
    } else if (combinedStatic > 0.8) {
      // Bright pixels
      intensity = 0.8 + random3(pixelatedIntensity + time) * 0.15;
    } else if (combinedStatic > 0.6) {
      // Medium-bright pixels
      intensity = 0.5 + random1(pixelatedIntensity + time) * 0.3;
    } else if (combinedStatic > 0.3) {
      // Medium pixels
      intensity = 0.2 + random2(pixelatedIntensity + time) * 0.3;
    } else if (combinedStatic > 0.1) {
      // Dark pixels
      intensity = 0.05 + random3(pixelatedIntensity + time) * 0.15;
    } else {
      // Very dark pixels (most common)
      intensity = random1(pixelatedIntensity + time) * 0.08;
    }
    
    // Add scanlines for CRT effect
    float scanlineEffect = scanlines(uv, time);
    intensity += scanlineEffect;
    
    // Add phosphor glow effect (slight vertical blur simulation)
    float phosphorGlow = 0.0;
    for (int i = -1; i <= 1; i++) {
      vec2 offsetUv = uv + vec2(0.0, float(i) * 0.001);
      phosphorGlow += random1(pixelate(offsetUv, 35.0) + time) * 0.1;
    }
    intensity += phosphorGlow * 0.02;
    
    // Add interlacing effect (alternating line intensity)
    float interlacing = mod(floor(uv.y * 600.0), 2.0) * 0.03;
    intensity += interlacing;
    
    // Add subtle color variation with random timing and pixelation (slower for CRT feel)
    vec3 staticColor = vec3(intensity);
    vec2 pixelatedColor = pixelate(uv, 50.0); // Medium pixels for color variation
    float colorTime = time + random1(pixelatedColor) * 2.0; // Much slower random color timing
    staticColor.r += sin(colorTime * 0.5 + pixelatedColor.x * 8.0) * 0.015; // Much slower color changes
    staticColor.g += cos(colorTime * 0.4 + pixelatedColor.y * 6.0) * 0.015; // Much slower color changes
    staticColor.b += sin(colorTime * 0.6 + pixelatedColor.x * 4.0) * 0.015; // Much slower color changes
    
    // Clamp colors
    staticColor = clamp(staticColor, 0.0, 1.0);
    
    // Overlay logo if available
    if (hasLogo) {
      // Create smaller logo UV coordinates (centered and scaled down)
      vec2 logoUv = (uv - vec2(0.5, 0.5)) * 1.5 + vec2(0.5, 0.5); // Make logo smaller by scaling UV coordinates
      
      // Fix vertical flip by inverting Y coordinate
      logoUv.y = 1.0 - logoUv.y;
      
      // Only apply logo if within the scaled bounds
      if (logoUv.x >= 0.0 && logoUv.x <= 1.0 && logoUv.y >= 0.0 && logoUv.y <= 1.0) {
        vec4 logoColor = texture2D(logoTexture, logoUv);
        if (logoColor.a > 0.1) {
          // Blend logo with static, using logoOpacity for fade-in effect
          float logoBlend = logoColor.a * 0.85 * logoOpacity;
          staticColor = mix(staticColor, logoColor.rgb, logoBlend);
          // Add some static on top of the logo for realism
          staticColor += (1.0 - logoBlend) * staticColor * 0.15;
        }
      }
    }
    
    gl_FragColor = vec4(staticColor, 1.0);
  }
`;

// Create shader material for TV static
const createStaticShaderMaterial = (logoTexture, time, textureRepeat = 4.6, textureOffset = { x: -3.13, y: -1.05 }, logoOpacity = 1.0) => {
  return new THREE.ShaderMaterial({
    vertexShader: staticVertexShader,
    fragmentShader: staticFragmentShader,
    uniforms: {
      time: { value: time },
      logoTexture: { value: logoTexture },
      hasLogo: { value: logoTexture !== null },
      logoOpacity: { value: logoOpacity },
      textureRepeat: { value: textureRepeat },
      textureOffset: { value: new THREE.Vector2(textureOffset.x, textureOffset.y) }
    },
    transparent: false
  });
};

// TODO: Implement dynamic quality selection using getImagePath() with useConnectionSpeed()
// For now, using medium_quality as default
const useVideoTexture = ({
  videoSrc,
  placeholderImage: providedPlaceholderImage,
  showPlaceholderImage = true,
  onVideoLoad,
  onVideoError,
  muted = false,
  volume = 1.0,
  autoPlay = true
}) => {
  const { quality } = useConnectionSpeed();
  // Use provided placeholder or fall back to dynamic quality path
  const placeholderImage = providedPlaceholderImage || getImagePath('tv-placeholder.png', quality);
  const videoRef = useRef();
  const textureRef = useRef();
  const shaderMaterialRef = useRef();
  const logoTextureRef = useRef();
  const animationFrameRef = useRef();
  const lastVideoSrcRef = useRef(null);
  const animateStaticRef = useRef();
  const onVideoLoadRef = useRef();
  const onVideoErrorRef = useRef();
  const isInitializingRef = useRef(false);
  const playCalledRef = useRef(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [isStaticMode, setIsStaticMode] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const lastUpdateTimeRef = useRef(0);
  const logoFadeStartTimeRef = useRef(null);
  const { performanceSettings } = useGPUTier();

  // Store callbacks in refs to prevent unnecessary re-runs
  useEffect(() => {
    onVideoLoadRef.current = onVideoLoad;
    onVideoErrorRef.current = onVideoError;
  }, [onVideoLoad, onVideoError]);

  // Optimized animation loop for shader-based static with better performance
  const animateStatic = useCallback(() => {
    if (shaderMaterialRef.current && isStaticMode && isPageVisible) {
      const now = performance.now();
      // Use staticFPS only if it's lower than our target 20fps, otherwise use 20fps
      const targetFPS = Math.min(performanceSettings.staticFPS, 20);
      const frameInterval = 1000 / targetFPS;

      // Only update if enough time has passed
      if (now - lastUpdateTimeRef.current >= frameInterval) {
        // Use a slower time calculation to match CRT refresh rates
        const currentTime = now * 0.0005; // Half speed for CRT feel
        shaderMaterialRef.current.uniforms.time.value = currentTime;

        // Animate logo fade-in if fade is in progress
        if (logoFadeStartTimeRef.current !== null && shaderMaterialRef.current.uniforms.logoOpacity) {
          const fadeDuration = 1000; // 1 second fade-in
          const elapsed = now - logoFadeStartTimeRef.current;
          const fadeProgress = Math.min(elapsed / fadeDuration, 1.0);

          // Use ease-in-out for smooth fade
          const easedProgress = fadeProgress < 0.5
            ? 2 * fadeProgress * fadeProgress
            : 1 - Math.pow(-2 * fadeProgress + 2, 2) / 2;

          shaderMaterialRef.current.uniforms.logoOpacity.value = easedProgress;

          // Clear fade start time when complete
          if (fadeProgress >= 1.0) {
            logoFadeStartTimeRef.current = null;
          }
        }

        shaderMaterialRef.current.needsUpdate = true;
        lastUpdateTimeRef.current = now;
      }

      // Use setTimeout instead of requestAnimationFrame for better performance control
      setTimeout(() => {
        animationFrameRef.current = requestAnimationFrame(animateStatic);
      }, 16); // ~60fps max
    } else if (shaderMaterialRef.current && isStaticMode && !isPageVisible) {
      // Continue animation but at reduced frequency when page is not visible
      setTimeout(() => {
        animationFrameRef.current = requestAnimationFrame(animateStatic);
      }, 200); // Even slower when not visible (5 FPS)
    }
  }, [isStaticMode, isPageVisible, performanceSettings.staticFPS]);

  // Store animateStatic in a ref so we can use it without including it in dependency arrays
  useEffect(() => {
    animateStaticRef.current = animateStatic;
  }, [animateStatic]);

  useEffect(() => {
    // Helper function to normalize URLs for comparison (handle relative vs absolute paths)
    const normalizeUrl = (url) => {
      if (!url) return '';
      try {
        // If it's already a full URL, extract the pathname
        if (url.startsWith('http://') || url.startsWith('https://')) {
          const urlObj = new URL(url);
          return urlObj.pathname;
        }
        // If it's a relative path, ensure it starts with /
        return url.startsWith('/') ? url : '/' + url;
      } catch {
        return url;
      }
    };

    // Early return if video is already created with the same source
    // This prevents unnecessary recreation when the effect re-runs due to other dependencies
    if (videoSrc && videoRef.current && lastVideoSrcRef.current === videoSrc) {
      const currentSrc = normalizeUrl(videoRef.current.src);
      const normalizedVideoSrc = normalizeUrl(videoSrc);

      // If video exists with same source, don't recreate (even if not fully loaded yet)
      if (currentSrc === normalizedVideoSrc) {
        // Update muted and volume if they changed
        if (videoRef.current.muted !== muted) {
          videoRef.current.muted = muted;
        }
        if (Math.abs(videoRef.current.volume - volume) > 0.01) {
          videoRef.current.volume = Math.max(0, Math.min(1, volume));
        }

        // If video is ready, ensure it's playing (only if autoPlay is enabled)
        if (videoRef.current.readyState >= 3 && autoPlay) {
          if (videoRef.current.paused && isVideoLoaded && !isInitializingRef.current) {
            videoRef.current.play().catch(err => console.warn('Could not resume video:', err));
          }
        }
        isInitializingRef.current = false;
        return; // Don't recreate the video
      }
    }

    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current && videoSrc && lastVideoSrcRef.current === videoSrc) {
      logger.debug('Video initialization already in progress, skipping...');
      return;
    }

    if (!videoSrc) {
      // If no video source, create shader-based static with logo
      const createStaticPlaceholder = () => {
        // Only create material if it doesn't exist
        if (!shaderMaterialRef.current) {
          // Load logo texture only if showPlaceholderImage is true
          if (showPlaceholderImage) {
            const logoLoader = new THREE.TextureLoader();
            logoLoader.load(
              placeholderImage,
              (logoTexture) => {
                logoTexture.wrapS = THREE.ClampToEdgeWrapping;
                logoTexture.wrapT = THREE.ClampToEdgeWrapping;
                logoTexture.minFilter = THREE.LinearFilter;
                logoTexture.magFilter = THREE.LinearFilter;
                logoTextureRef.current = logoTexture;

                // Create shader material for static effect with proper UV scaling
                // Start with logoOpacity at 0 for fade-in effect
                const shaderMaterial = createStaticShaderMaterial(logoTexture, 0, 4.6, { x: -3.13, y: -1.05 }, 0.0);
                shaderMaterialRef.current = shaderMaterial;

                // Start fade-in animation
                logoFadeStartTimeRef.current = performance.now();

                // Create a dummy texture for compatibility
                const dummyTexture = new THREE.Texture();
                textureRef.current = dummyTexture;

                setIsStaticMode(true);
                if (animateStaticRef.current) animateStaticRef.current();

                logger.debug('TV shader-based static placeholder created with fade-in');

              },
              undefined,
              (error) => {
                console.warn('Failed to load logo texture, using static only:', error);

                // Create shader material without logo with proper UV scaling
                const shaderMaterial = createStaticShaderMaterial(null, 0, 4.6, { x: -3.13, y: -1.05 });
                shaderMaterialRef.current = shaderMaterial;

                // Create a dummy texture for compatibility
                const dummyTexture = new THREE.Texture();
                textureRef.current = dummyTexture;

                setIsStaticMode(true);
                if (animateStaticRef.current) animateStaticRef.current();

                logger.debug('TV shader-based static placeholder created (logo failed to load)');
              }
            );
          } else {
            // Create static without logo
            const shaderMaterial = createStaticShaderMaterial(null, 0, 4.6, { x: -3.13, y: -1.05 });
            shaderMaterialRef.current = shaderMaterial;

            // Create a dummy texture for compatibility
            const dummyTexture = new THREE.Texture();
            textureRef.current = dummyTexture;

            setIsStaticMode(true);
            if (animateStaticRef.current) animateStaticRef.current();

            logger.debug('TV shader-based static placeholder created (without logo)');
          }
        } else {
          // Material already exists, update it based on showPlaceholderImage
          if (shaderMaterialRef.current && shaderMaterialRef.current.uniforms) {
            if (showPlaceholderImage) {
              // Show placeholder - load texture if not already loaded
              if (!logoTextureRef.current) {
                const logoLoader = new THREE.TextureLoader();
                logoLoader.load(
                  placeholderImage,
                  (logoTexture) => {
                    logoTexture.wrapS = THREE.ClampToEdgeWrapping;
                    logoTexture.wrapT = THREE.ClampToEdgeWrapping;
                    logoTexture.minFilter = THREE.LinearFilter;
                    logoTexture.magFilter = THREE.LinearFilter;
                    logoTextureRef.current = logoTexture;

                    // Update material with logo texture
                    shaderMaterialRef.current.uniforms.logoTexture.value = logoTexture;
                    shaderMaterialRef.current.uniforms.hasLogo.value = true;
                    shaderMaterialRef.current.uniforms.logoOpacity.value = 0.0; // Start at 0 for fade-in
                    shaderMaterialRef.current.needsUpdate = true;

                    // Start fade-in animation
                    logoFadeStartTimeRef.current = performance.now();

                    logger.debug('Updated static material with logo texture - starting fade-in');
                  },
                  undefined,
                  (error) => {
                    console.warn('Failed to load logo texture:', error);
                  }
                );
              } else {
                // Texture already loaded, just update material and start fade-in
                shaderMaterialRef.current.uniforms.logoTexture.value = logoTextureRef.current;
                shaderMaterialRef.current.uniforms.hasLogo.value = true;
                shaderMaterialRef.current.uniforms.logoOpacity.value = 0.0; // Start at 0 for fade-in
                shaderMaterialRef.current.needsUpdate = true;

                // Start fade-in animation
                logoFadeStartTimeRef.current = performance.now();

                logger.debug('Updated static material with existing logo texture - starting fade-in');
              }
            } else {
              // Hide placeholder - clear texture and update material
              // Dispose old texture if it exists and clear cache
              if (shaderMaterialRef.current.uniforms.logoTexture.value) {
                const oldTexture = shaderMaterialRef.current.uniforms.logoTexture.value;
                if (oldTexture.dispose) {
                  oldTexture.dispose();
                }
              }

              // Clear the texture ref cache
              if (logoTextureRef.current) {
                if (logoTextureRef.current.dispose) {
                  logoTextureRef.current.dispose();
                }
                logoTextureRef.current = null;
              }

              // Stop fade animation
              logoFadeStartTimeRef.current = null;

              shaderMaterialRef.current.uniforms.logoTexture.value = null;
              shaderMaterialRef.current.uniforms.hasLogo.value = false;
              shaderMaterialRef.current.uniforms.logoOpacity.value = 0.0;
              shaderMaterialRef.current.needsUpdate = true;

              logger.debug('Updated static material - removed logo texture and cleared cache');
            }
          }

          setIsStaticMode(true);
          if (animateStaticRef.current) animateStaticRef.current();
        }
      };

      createStaticPlaceholder();

      // Cleanup function
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        setIsStaticMode(false);
      };
    }

    // This check is redundant now (handled above), but keeping for safety
    // Don't recreate video if it already exists with the same source
    const currentSrc = videoRef.current?.src ? normalizeUrl(videoRef.current.src) : '';
    const normalizedVideoSrc = normalizeUrl(videoSrc);

    if (videoRef.current && currentSrc && currentSrc === normalizedVideoSrc && lastVideoSrcRef.current === videoSrc) {
      // Video already exists with same source, don't recreate
      isInitializingRef.current = false;
      return;
    }

    // Clean up old video if source changed
    if (videoRef.current && lastVideoSrcRef.current !== null && lastVideoSrcRef.current !== videoSrc) {
      logger.debug('Video source changed, cleaning up old video');
      isInitializingRef.current = false;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
      if (textureRef.current) {
        textureRef.current.dispose();
      }
      setIsVideoLoaded(false);
      setHasVideoError(false);
    }

    // Mark that we're initializing and reset play flag
    isInitializingRef.current = true;
    playCalledRef.current = false;

    // Update the ref to track current source
    lastVideoSrcRef.current = videoSrc;

    // Create video element
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = muted;
    video.volume = Math.max(0, Math.min(1, volume)); // Clamp volume between 0 and 1
    video.playsInline = true;
    // Use 'metadata' instead of 'auto' to enable streaming - only loads metadata, not entire video
    video.preload = 'metadata';

    // Set video source
    video.src = videoSrc;

    // Create video texture
    const videoTexture = new THREE.VideoTexture(video);
    videoTexture.wrapS = THREE.ClampToEdgeWrapping;
    videoTexture.wrapT = THREE.ClampToEdgeWrapping;
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;
    videoTexture.colorSpace = THREE.SRGBColorSpace; // Ensure proper color space for video

    // VideoTexture updates automatically when video plays, but ensure it's enabled
    videoTexture.needsUpdate = true;

    // Set texture offset and repeat to fit the screen properly
    videoTexture.offset.set(0, 0);
    videoTexture.repeat.set(1, 1);

    textureRef.current = videoTexture;
    videoRef.current = video;

    // Reset loaded state when creating new video
    setIsVideoLoaded(false);
    setHasVideoError(false);

    // Event handlers
    const handleError = (error) => {
      console.warn('TV video failed to load:', error);
      setHasVideoError(true);

      // Fallback to shader-based static placeholder with logo
      const createFallbackPlaceholder = () => {
        // Only create material if it doesn't exist
        if (!shaderMaterialRef.current) {
          // Load logo texture
          const logoLoader = new THREE.TextureLoader();
          logoLoader.load(
            placeholderImage,
            (logoTexture) => {
              logoTexture.wrapS = THREE.ClampToEdgeWrapping;
              logoTexture.wrapT = THREE.ClampToEdgeWrapping;
              logoTexture.minFilter = THREE.LinearFilter;
              logoTexture.magFilter = THREE.LinearFilter;
              logoTextureRef.current = logoTexture;

              // Create shader material for static effect with proper UV scaling
              const shaderMaterial = createStaticShaderMaterial(logoTexture, 0, 4.6, { x: -3.13, y: -1.05 });
              shaderMaterialRef.current = shaderMaterial;

              // Create a dummy texture for compatibility
              const dummyTexture = new THREE.Texture();
              textureRef.current = dummyTexture;

              setIsStaticMode(true);
              if (animateStaticRef.current) animateStaticRef.current();

              logger.debug('TV fallback shader-based static placeholder with logo overlay created');
            },
            undefined,
            (fallbackError) => {
              console.warn('Failed to load logo texture for fallback, using static only:', fallbackError);

              // Create shader material without logo with proper UV scaling
              const shaderMaterial = createStaticShaderMaterial(null, 0, 4.6, { x: -3.13, y: -1.05 });
              shaderMaterialRef.current = shaderMaterial;

              // Create a dummy texture for compatibility
              const dummyTexture = new THREE.Texture();
              textureRef.current = dummyTexture;

              setIsStaticMode(true);
              if (animateStaticRef.current) animateStaticRef.current();

              logger.debug('TV fallback shader-based static placeholder created (logo failed to load)');
            }
          );
        } else {
          // Material already exists, just start animation
          setIsStaticMode(true);
          if (animateStaticRef.current) animateStaticRef.current();
        }
      };

      createFallbackPlaceholder();

      isInitializingRef.current = false;
      if (onVideoErrorRef.current) {
        onVideoErrorRef.current(error);
      }
    };

    const markVideoAsLoaded = () => {
      // Only mark as loaded if video has actual frame data
      // videoWidth and videoHeight indicate the video has decoded frames
      if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 3) {
        setIsVideoLoaded(true);
        logger.debug('TV video loaded successfully with frame data:', videoSrc, {
          width: video.videoWidth,
          height: video.videoHeight,
          readyState: video.readyState
        });
        isInitializingRef.current = false;
        if (onVideoLoadRef.current) {
          onVideoLoadRef.current(video);
        }
      }
    };

    const handleCanPlay = async () => {
      // Prevent multiple play() calls
      if (playCalledRef.current || !isInitializingRef.current) {
        return;
      }

      // Wait for video to have enough data to play
      // Check if video has actual frame data (readyState >= 3 means HAVE_FUTURE_DATA)
      if (video.readyState >= 3) {
        // Only auto-play if autoPlay is enabled
        if (!autoPlay) {
          // Mark video as loaded even if not playing
          markVideoAsLoaded();
          return;
        }

        try {
          // Only call play() if video is paused
          if (video.paused) {
            playCalledRef.current = true;
            await video.play();
            logger.debug('Video play() called successfully, playing:', !video.paused, 'currentTime:', video.currentTime, 'muted:', video.muted);
          }

          // Wait a bit to ensure video has actual frames before marking as loaded
          // Use a small delay to ensure WebGL can access video frames
          setTimeout(() => {
            // Verify video is actually playing
            if (!video.paused && video.readyState >= 3) {
              markVideoAsLoaded();
              // Ensure texture updates
              if (textureRef.current) {
                textureRef.current.needsUpdate = true;
              }
            } else {
              logger.debug('Video not playing yet, waiting...', { paused: video.paused, readyState: video.readyState });
            }
          }, 100);
        } catch (error) {
          // If autoplay fails with audio, try muting temporarily
          if (!muted && error.name === 'NotAllowedError') {
            logger.debug('Autoplay with audio blocked, attempting muted playback...');
            const originalMuted = video.muted;
            video.muted = true;
            try {
              await video.play();
              // Unmute after play starts (user interaction may be required)
              video.muted = originalMuted;
              logger.debug('Video started muted, unmuted after play');
            } catch (mutedError) {
              console.warn('Could not autoplay video even when muted:', mutedError);
            }
          } else {
            console.warn('Could not autoplay video:', error);
          }
          playCalledRef.current = false; // Reset on error so we can retry
          // Even if autoplay fails, check if video has frame data
          setTimeout(() => {
            markVideoAsLoaded();
          }, 100);
        }
      }
    };

    const handlePlaying = () => {
      // Video is actually playing now, safe to use in WebGL
      logger.debug('Video playing event fired', { currentTime: video.currentTime, paused: video.paused });
      // Wait a tiny bit to ensure frames are available
      setTimeout(() => {
        markVideoAsLoaded();
        // Ensure texture updates when video starts playing
        if (textureRef.current) {
          textureRef.current.needsUpdate = true;
        }
      }, 50);
    };

    // Add event listeners (only once per video element)
    // Remove any existing listeners first to prevent duplicates
    video.removeEventListener('error', handleError);
    video.removeEventListener('canplay', handleCanPlay);
    video.removeEventListener('playing', handlePlaying);

    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);

    // Load the video
    video.load();

    // Cleanup
    return () => {
      // Clean up event listeners
      if (video) {
        video.removeEventListener('error', handleError);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('playing', handlePlaying);
      }

      // Reset play flag
      playCalledRef.current = false;

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
        videoRef.current = null;
      }

      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }

      // Clean up shader animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (shaderMaterialRef.current) {
        shaderMaterialRef.current.dispose();
        shaderMaterialRef.current = null;
      }

      if (logoTextureRef.current) {
        logoTextureRef.current.dispose();
        logoTextureRef.current = null;
      }

      setIsStaticMode(false);
      isInitializingRef.current = false;
    };
    // Intentionally excluding 'volume' and 'isVideoLoaded' to prevent video reload loops.
    // 'volume' is handled by a separate useEffect (lines 809-819) that updates the video element directly.
    // 'isVideoLoaded' is internal state that shouldn't trigger video recreation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSrc, placeholderImage, autoPlay, showPlaceholderImage, muted]);

  // Update muted and volume without recreating the video
  useEffect(() => {
    if (videoRef.current) {
      if (videoRef.current.muted !== muted) {
        videoRef.current.muted = muted;
      }
      if (Math.abs(videoRef.current.volume - volume) > 0.01) {
        videoRef.current.volume = Math.max(0, Math.min(1, volume));
      }
    }
  }, [muted, volume]);

  // Page visibility optimization
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Ensure static animation runs when in static mode
  useEffect(() => {
    if (isStaticMode && shaderMaterialRef.current) {
      // Start the animation loop if it's not already running
      if (!animationFrameRef.current && animateStaticRef.current) {
        logger.debug('Starting static animation loop');
        animateStaticRef.current();
      }
    } else {
      // Stop animation when not in static mode
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      // Cleanup on unmount or when static mode changes
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isStaticMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (shaderMaterialRef.current) {
        shaderMaterialRef.current.dispose();
        shaderMaterialRef.current = null;
      }

      if (logoTextureRef.current) {
        logoTextureRef.current.dispose();
        logoTextureRef.current = null;
      }

      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }

      // Reset timing reference
      lastUpdateTimeRef.current = 0;
    };
  }, []);

  // Update video texture in animation loop
  // Note: VideoTexture should update automatically, but we ensure it does
  useEffect(() => {
    if (!isVideoLoaded || !textureRef.current || !videoRef.current) {
      return;
    }

    const updateTexture = () => {
      if (textureRef.current && videoRef.current && !videoRef.current.paused) {
        // VideoTexture updates automatically, but we ensure needsUpdate is set
        textureRef.current.needsUpdate = true;
      }
    };

    // Use requestAnimationFrame for smoother updates
    let animationFrameId;
    const loop = () => {
      updateTexture();
      animationFrameId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isVideoLoaded]);

  return {
    texture: textureRef.current,
    video: videoRef.current,
    shaderMaterial: shaderMaterialRef.current,
    isVideoLoaded,
    hasVideoError,
    isStaticMode
  };
};

export default useVideoTexture;